import React, { FC, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography, Grid, Skeleton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

interface NetworkConfig {
	ethernet?: {
		connected: boolean;
		ip?: string;
		gateway?: string;
		connection?: string;
		macAddress?: string;
		dhcpEnabled?: boolean;
		state?: string;
		device?: string;
	};
	wifi?: {
		connected: boolean;
		ip?: string;
		gateway?: string;
		ssid?: string;
		signal?: number;
		security?: string;
		macAddress?: string;
		state?: string;
		device?: string;
	};
	gateway?: string;
}

const CurrentNetworkStatus: FC = () => {
	const [networkConfig, setNetworkConfig] = useState<NetworkConfig | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [refreshing, setRefreshing] = useState<boolean>(false);

	const loadCurrentStatus = async () => {
		try {
			setLoading(true);
			const response = await fetch(`http://${window.location.hostname}:81/api/network-config`);
			const data = await response.json();

			if (response.ok) {
				setNetworkConfig(data);
			} else {
				throw new Error(data.error || "Failed to load network configuration");
			}
		} catch (error) {
			console.error("Error loading network configuration:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleRefresh = async () => {
		setRefreshing(true);
		await loadCurrentStatus();
		setRefreshing(false);
	};

	useEffect(() => {
		loadCurrentStatus();
	}, []);

	const getEthernetStatus = () => {
		if (!networkConfig?.ethernet) return null;

		const { ethernet } = networkConfig;

		if (ethernet.connected) {
			return (
				<Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
					<Box className="network-setting-wrapper column">
						<Box className="network-setting-text-wrapper">
							<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
								Ethernet Connection
							</Typography>
							<Typography variant="body2" color="#616161">
								Wired network interface status
							</Typography>
						</Box>

						<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									IP:
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{ethernet.ip || "N/A"}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									Gateway:
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{ethernet.gateway || "N/A"}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									Connection:
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{ethernet.connection || "N/A"}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									MAC:
								</Typography>
								<Typography variant="body2" fontWeight={500} fontFamily="monospace">
									{ethernet.macAddress || "N/A"}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									DHCP:
								</Typography>
								<Box
									sx={{
										display: "inline-flex",
										alignItems: "center",
										padding: "4px 8px",
										borderRadius: "12px",
										border: "1px solid",
										borderColor: ethernet.dhcpEnabled ? "#2e7d32" : "#666666",
										backgroundColor: ethernet.dhcpEnabled ? "rgba(46, 125, 50, 0.08)" : "rgba(102, 102, 102, 0.08)",
										minWidth: "fit-content"
									}}
								>
									<Typography
										variant="body2"
										fontWeight={500}
										color={ethernet.dhcpEnabled ? "#2e7d32" : "#666666"}
										sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
									>
										{ethernet.dhcpEnabled ? "Enabled" : "Disabled"}
									</Typography>
								</Box>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									Status:
								</Typography>
								<Box
									sx={{
										display: "inline-flex",
										alignItems: "center",
										padding: "4px 8px",
										borderRadius: "12px",
										border: "1px solid #2e7d32",
										backgroundColor: "rgba(46, 125, 50, 0.08)",
										minWidth: "fit-content"
									}}
								>
									<Typography
										variant="body2"
										fontWeight={500}
										color="#2e7d32"
										sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
									>
										Connected
									</Typography>
								</Box>
							</Box>
						</Box>
					</Box>
				</Grid>
			);
		} else {
			let status = "Disconnected";
			if (ethernet.state === "no_ethernet_device") status = "No Ethernet Device";
			else if (ethernet.state === "error") status = "Error";

			return (
				<Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
					<Box className="network-setting-wrapper column">
						<Box className="network-setting-text-wrapper">
							<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
								Ethernet Connection
							</Typography>
							<Typography variant="body2" color="#616161">
								Wired network interface status
							</Typography>
						</Box>

						<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									Status:
								</Typography>
								<Box
									sx={{
										display: "inline-flex",
										alignItems: "center",
										padding: "4px 8px",
										borderRadius: "12px",
										border: "1px solid #d32f2f",
										backgroundColor: "rgba(211, 47, 47, 0.08)",
										minWidth: "fit-content"
									}}
								>
									<Typography
										variant="body2"
										fontWeight={500}
										color="#d32f2f"
										sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
									>
										{status}
									</Typography>
								</Box>
							</Box>
						</Box>
					</Box>
				</Grid>
			);
		}
	};

	const getWifiStatus = () => {
		if (!networkConfig?.wifi) return null;

		const { wifi } = networkConfig;

		if (wifi.connected) {
			return (
				<Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
					<Box className="network-setting-wrapper column">
						<Box className="network-setting-text-wrapper">
							<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
								WiFi Connection
							</Typography>
							<Typography variant="body2" color="#616161">
								Wireless network interface status
							</Typography>
						</Box>

						<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									IP:
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{wifi.ip || "N/A"}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									Gateway:
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{wifi.gateway || "N/A"}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									Wifi Connection:
								</Typography>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Typography variant="body2" fontWeight={500} fontFamily="monospace">
										{wifi.ssid || "N/A"}
									</Typography>
									{wifi.security && wifi.security !== "--" && (
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												padding: "2px 6px",
												borderRadius: "8px",
												backgroundColor: "rgba(139, 69, 19, 0.1)",
												border: "1px solid rgba(139, 69, 19, 0.3)",
												minWidth: "fit-content"
											}}
										>
											<Typography
												variant="body2"
												fontWeight={500}
												color="#8B4513"
												sx={{ fontSize: "0.7rem", lineHeight: 1.2, textTransform: "uppercase" }}
											>
												{wifi.security}
											</Typography>
										</Box>
									)}
									{wifi.security === "--" && (
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												padding: "2px 6px",
												borderRadius: "8px",
												backgroundColor: "rgba(139, 69, 19, 0.1)",
												border: "1px solid rgba(139, 69, 19, 0.3)",
												minWidth: "fit-content"
											}}
										>
											<Typography
												variant="body2"
												fontWeight={500}
												color="#8B4513"
												sx={{ fontSize: "0.7rem", lineHeight: 1.2, textTransform: "uppercase" }}
											>
												OPEN
											</Typography>
										</Box>
									)}
									<Typography variant="body2" color="#616161" sx={{ marginLeft: 0.5 }}>
										-
									</Typography>
									<Typography variant="body2" fontWeight={500} fontFamily="monospace">
										({wifi.signal || "N/A"}%)
									</Typography>
								</Box>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									MAC:
								</Typography>
								<Typography variant="body2" fontWeight={500} fontFamily="monospace">
									{wifi.macAddress || "N/A"}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									Status:
								</Typography>
								<Box
									sx={{
										display: "inline-flex",
										alignItems: "center",
										padding: "4px 8px",
										borderRadius: "12px",
										border: "1px solid #2e7d32",
										backgroundColor: "rgba(46, 125, 50, 0.08)",
										minWidth: "fit-content"
									}}
								>
									<Typography
										variant="body2"
										fontWeight={500}
										color="#2e7d32"
										sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
									>
										Connected
									</Typography>
								</Box>
							</Box>
						</Box>
					</Box>
				</Grid>
			);
		} else {
			let status = "Disconnected";
			if (wifi.state === "no_wifi_device") status = "No WiFi Device";
			else if (wifi.state === "error") status = "Error";

			return (
				<Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
					<Box className="network-setting-wrapper column">
						<Box className="network-setting-text-wrapper">
							<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
								WiFi Connection
							</Typography>
							<Typography variant="body2" color="#616161">
								Wireless network interface status
							</Typography>
						</Box>

						<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="#616161">
									Status:
								</Typography>
								<Box
									sx={{
										display: "inline-flex",
										alignItems: "center",
										padding: "4px 8px",
										borderRadius: "12px",
										border: "1px solid #d32f2f",
										backgroundColor: "rgba(211, 47, 47, 0.08)",
										minWidth: "fit-content"
									}}
								>
									<Typography
										variant="body2"
										fontWeight={500}
										color="#d32f2f"
										sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
									>
										{status}
									</Typography>
								</Box>
							</Box>
						</Box>
					</Box>
				</Grid>
			);
		}
	};

	return (
		<Box
			className="network-setting-section-wrapper"
			sx={{
				background: "#ffffff",
				borderRadius: "12px",
				boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
				border: "1px solid rgba(0, 0, 0, 0.06)"
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%"
				}}
			>
				<Typography variant="h6" color="var(--color-primary-main)">
					Current Network Status
				</Typography>

				<Button
					variant="outlined"
					color="primary"
					startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
					onClick={handleRefresh}
					disabled={refreshing}
				>
					Refresh
				</Button>
			</Box>

			{loading ? (
				<Grid container spacing={{ xs: 2, sm: 3 }}>
					{/* Ethernet Status Skeleton */}
					<Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
						<Box className="network-setting-wrapper column">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Ethernet Connection
								</Typography>
								<Typography variant="body2" color="#616161">
									Wired network interface status
								</Typography>
							</Box>

							<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
								{/* IP skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={30} height={20} />
									<Skeleton variant="text" width={120} height={20} />
								</Box>

								{/* Gateway skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={70} height={20} />
									<Skeleton variant="text" width={120} height={20} />
								</Box>

								{/* Connection skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={90} height={20} />
									<Skeleton variant="text" width={100} height={20} />
								</Box>

								{/* MAC skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={40} height={20} />
									<Skeleton variant="text" width={140} height={20} />
								</Box>

								{/* DHCP skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={50} height={20} />
									<Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "12px" }} />
								</Box>

								{/* Status skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={50} height={20} />
									<Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: "12px" }} />
								</Box>
							</Box>
						</Box>
					</Grid>

					{/* WiFi Status Skeleton */}
					<Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
						<Box className="network-setting-wrapper column">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									WiFi Connection
								</Typography>
								<Typography variant="body2" color="#616161">
									Wireless network interface status
								</Typography>
							</Box>

							<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
								{/* IP skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={30} height={20} />
									<Skeleton variant="text" width={120} height={20} />
								</Box>

								{/* Gateway skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={70} height={20} />
									<Skeleton variant="text" width={120} height={20} />
								</Box>

								{/* WiFi Connection skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={120} height={20} />
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Skeleton variant="text" width={100} height={20} />
										<Skeleton variant="rectangular" width={50} height={20} sx={{ borderRadius: "8px" }} />
										<Skeleton variant="text" width={10} height={20} />
										<Skeleton variant="text" width={40} height={20} />
									</Box>
								</Box>

								{/* MAC skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={40} height={20} />
									<Skeleton variant="text" width={140} height={20} />
								</Box>

								{/* Status skeleton */}
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Skeleton variant="text" width={50} height={20} />
									<Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: "12px" }} />
								</Box>
							</Box>
						</Box>
					</Grid>
				</Grid>
			) : (
				<Grid container spacing={{ xs: 2, sm: 3 }}>
					{getEthernetStatus()}
					{getWifiStatus()}
				</Grid>
			)}
		</Box>
	);
};

export default CurrentNetworkStatus;
